// Lightweight geodesy helpers — great circle distance & bearings.
//
// All angles in/out: DEGREES. All distances out: NAUTICAL MILES.
// Bearings returned are TRUE bearings (not magnetic). Magnetic conversion is
// a separate concern (per-airport magVar field, or a model like WMM).
//
// Cross-checked against an independent OpenAI Codex implementation
// 2026-05-08 — math agrees to floating-point precision on 3 reference cases.

// Earth radius — WGS-84 mean radius converted to nautical miles.
const R_NM = 3440.065;

const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Normalize a longitude difference to (-180, 180] for clarity / antimeridian sanity. */
function normalizeLonDelta(deg: number): number {
  let d = ((deg + 180) % 360) - 180;
  // JS modulo can yield -180 for inputs that should be 180; bump to canonical range.
  if (d <= -180) d += 360;
  return d;
}

/**
 * Great-circle distance in nautical miles, haversine.
 * Numerically stable; clamps to handle near-antipodal floating-point noise.
 */
export function greatCircleNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const dφ = toRad(lat2 - lat1);
  const dλ = toRad(normalizeLonDelta(lon2 - lon1));
  const a =
    Math.sin(dφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * R_NM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Initial **true** bearing along the great circle from (lat1,lon1) to (lat2,lon2).
 * Returns 0..360. Returns NaN if the two points coincide (no defined course).
 */
export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  // Coincident points — bearing is undefined; return NaN so callers can guard.
  if (lat1 === lat2 && lon1 === lon2) return Number.NaN;

  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(normalizeLonDelta(lon2 - lon1));
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Convert true bearing to magnetic, given local magnetic variation in degrees east-positive. */
export function trueToMag(trueDeg: number, magVarDeg: number): number {
  return ((trueDeg - magVarDeg) % 360 + 360) % 360;
}

/**
 * Compute the point at fraction `f` (0..1) along the great-circle from
 * (lat1,lon1) to (lat2,lon2). Uses the standard slerp on the unit sphere
 * (Williams' "Aviation Formulary" form). At f=0 returns the origin;
 * at f=1 returns the destination. Returns NaN coords for coincident
 * endpoints — caller should special-case zero-length legs.
 */
export function greatCircleInterp(
  lat1: number, lon1: number,
  lat2: number, lon2: number,
  f: number,
): { lat: number; lon: number } {
  if (lat1 === lat2 && lon1 === lon2) return { lat: lat1, lon: lon1 };
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const λ1 = toRad(lon1);
  const λ2 = toRad(lon2);
  // Angular distance d (radians) via haversine, sin-clamped for stability.
  const dφ = φ2 - φ1;
  const dλ = toRad(normalizeLonDelta(lon2 - lon1));
  const a = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  const d = 2 * Math.asin(Math.min(1, Math.sqrt(a)));
  if (d === 0) return { lat: lat1, lon: lon1 };

  const A = Math.sin((1 - f) * d) / Math.sin(d);
  const B = Math.sin(f * d) / Math.sin(d);
  const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
  const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
  const z = A * Math.sin(φ1) + B * Math.sin(φ2);
  const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
  const lon = Math.atan2(y, x);
  return { lat: toDeg(lat), lon: toDeg(lon) };
}

/**
 * Densify a polyline into a great-circle path with a vertex at most
 * every `stepNm` nautical miles. Maximum segment length keeps the line
 * smooth on the Web Mercator projection used by every chart tile
 * service — a plain LineString between two distant points renders as a
 * straight line in pixel space, which 1) doesn't follow the actual
 * route, and 2) creates apparent gaps where the rasterizer skips tiles
 * the line should have crossed. Densification fixes both at once.
 *
 * Returns one [lon, lat] array suitable for a GeoJSON LineString.
 */
export function densifyRouteForGreatCircle(
  points: Array<{ lat: number; lon: number }>,
  stepNm = 25,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (points.length === 0) return out;
  out.push([points[0].lon, points[0].lat]);
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const distNm = greatCircleNm(a.lat, a.lon, b.lat, b.lon);
    // For very short legs (< ~5 nm) the straight Mercator line is
    // visually indistinguishable from the geodesic. Skip the work.
    if (distNm < 5 || !Number.isFinite(distNm)) {
      out.push([b.lon, b.lat]);
      continue;
    }
    const segs = Math.max(2, Math.ceil(distNm / stepNm));
    for (let s = 1; s < segs; s++) {
      const f = s / segs;
      const p = greatCircleInterp(a.lat, a.lon, b.lat, b.lon, f);
      out.push([p.lon, p.lat]);
    }
    out.push([b.lon, b.lat]);
  }
  return out;
}

/**
 * Destination point given a start, true bearing (deg), and distance in
 * nautical miles. Direct great-circle formula (Williams' Aviation
 * Formulary). Returns { lat, lon } in degrees. Used to build geodesic
 * circles around the ownship for range-ring rendering.
 */
export function destinationPoint(
  lat: number, lon: number, brgDeg: number, distNm: number,
): { lat: number; lon: number } {
  const δ = distNm / R_NM; // angular distance in radians
  const θ = toRad(brgDeg);
  const φ1 = toRad(lat);
  const λ1 = toRad(lon);
  const sinφ2 = Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
  const φ2 = Math.asin(Math.min(1, Math.max(-1, sinφ2)));
  const y = Math.sin(θ) * Math.sin(δ) * Math.cos(φ1);
  const x = Math.cos(δ) - Math.sin(φ1) * sinφ2;
  const λ2 = λ1 + Math.atan2(y, x);
  // Normalize lon to (-180, 180].
  let lonOut = toDeg(λ2);
  lonOut = ((lonOut + 540) % 360) - 180;
  return { lat: toDeg(φ2), lon: lonOut };
}

/**
 * Build a geodesic circle of radius `radiusNm` around (lat, lon) as a
 * closed [lon, lat] ring with `segments` vertices (default 64). The
 * first and last points are identical so the ring is GeoJSON-valid.
 */
export function geodesicCircleRing(
  lat: number, lon: number, radiusNm: number, segments = 64,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (let i = 0; i < segments; i++) {
    const brg = (360 * i) / segments;
    const p = destinationPoint(lat, lon, brg, radiusNm);
    out.push([p.lon, p.lat]);
  }
  // Close the ring.
  out.push(out[0]);
  return out;
}

/**
 * Dead-reckon a target's position forward from an anchor point given
 * its groundspeed (kt) and true track (deg) and the elapsed time
 * since the anchor was observed (ms). Used by the ADS-B traffic
 * renderer to interpolate motion between poll cycles — the wire data
 * is 3-5 sec stale at best, but on screen targets move continuously.
 *
 * Standalone wrapper around destinationPoint() so callers don't have
 * to do the kt·sec → nm math themselves.
 */
export function extrapolatePosition(
  lat: number, lon: number,
  groundspeedKt: number, trackDeg: number,
  elapsedMs: number,
): { lat: number; lon: number } {
  if (!Number.isFinite(groundspeedKt) || groundspeedKt <= 0) {
    return { lat, lon };
  }
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    return { lat, lon };
  }
  // gs (nm/h) × hours = nm.
  const distNm = groundspeedKt * (elapsedMs / 3_600_000);
  // Tracks reported as NaN (rare) shouldn't crash; treat as 0 (N).
  const track = Number.isFinite(trackDeg) ? trackDeg : 0;
  return destinationPoint(lat, lon, track, distNm);
}

export function formatNm(nm: number) {
  if (!Number.isFinite(nm)) return '—';
  if (nm < 10) return nm.toFixed(1) + ' nm';
  return Math.round(nm) + ' nm';
}

export function formatBrg(d: number) {
  if (!Number.isFinite(d)) return '—';
  return Math.round(d).toString().padStart(3, '0') + '°';
}

/** Ray-cast point-in-polygon test against a GeoJSON ring (array of
 *  [lon, lat] coordinates, first === last per RFC 7946). */
function pointInRing(lon: number, lat: number, ring: Array<[number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect = ((yi > lat) !== (yj > lat))
      && (lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Test whether (lon, lat) is inside a GeoJSON Polygon or MultiPolygon.
 *  Honors interior rings (holes). Returns false for any other geometry
 *  type. Naive O(n) over vertices — fast enough for the 133-feature
 *  national dispatch-zone set even on every GPS fix. */
export function pointInGeoJsonPolygon(
  lon: number,
  lat: number,
  geom: GeoJSON.Geometry | null | undefined,
): boolean {
  if (!geom) return false;
  if (geom.type === 'Polygon') {
    const rings = (geom as GeoJSON.Polygon).coordinates as Array<Array<[number, number]>>;
    if (rings.length === 0) return false;
    if (!pointInRing(lon, lat, rings[0])) return false;
    // Inside outer ring; subtract any hole.
    for (let i = 1; i < rings.length; i++) {
      if (pointInRing(lon, lat, rings[i])) return false;
    }
    return true;
  }
  if (geom.type === 'MultiPolygon') {
    const polys = (geom as GeoJSON.MultiPolygon).coordinates as Array<Array<Array<[number, number]>>>;
    for (const rings of polys) {
      if (rings.length === 0) continue;
      if (!pointInRing(lon, lat, rings[0])) continue;
      let inHole = false;
      for (let i = 1; i < rings.length; i++) {
        if (pointInRing(lon, lat, rings[i])) { inHole = true; break; }
      }
      if (!inHole) return true;
    }
    return false;
  }
  return false;
}
